import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Portfolio.css';

interface Project {
  id: string;
  category: string;
  image: string;
  title: string;
  tags: string[];
  description: string;
}

const projects: Project[] = [
  {
    id: 'ecommerce-platform',
    category: 'ecommerce',
    image: '/images/portfolio/ecommerce-platform.jpg',
    title: 'Fashion E-commerce Platform',
    tags: ['E-commerce', 'React', 'Node.js'],
    description: 'A full-featured e-commerce platform with advanced product management, real-time inventory, and secure payment processing.'
  },
  {
    id: 'enterprise-dashboard',
    category: 'enterprise',
    image: '/images/portfolio/enterprise-dashboard.jpg',
    title: 'Enterprise Management Dashboard',
    tags: ['Enterprise', 'Angular', 'Python'],
    description: 'Custom enterprise dashboard with real-time analytics, reporting, and team collaboration features.'
  },
  {
    id: 'fitness-app',
    category: 'mobile',
    image: '/images/portfolio/fitness-app.jpg',
    title: 'Fitness Tracking Application',
    tags: ['Mobile', 'React Native', 'Firebase'],
    description: 'Cross-platform fitness app with workout tracking, nutrition planning, and social features.'
  },
  {
    id: 'education-platform',
    category: 'web',
    image: '/images/portfolio/education-platform.jpg',
    title: 'Online Learning Platform',
    tags: ['Web', 'Vue.js', 'Laravel'],
    description: 'Interactive learning platform with video courses, quizzes, and progress tracking.'
  }
];

const Portfolio: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  return (
    <div className="portfolio-page">
      <section className="portfolio-hero">
        <h1>Our Portfolio</h1>
        <p>Innovative Solutions for Modern Businesses</p>
      </section>

      <section className="portfolio-filters">
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            All Projects
          </button>
          <button 
            className={`filter-btn ${activeFilter === 'web' ? 'active' : ''}`}
            onClick={() => setActiveFilter('web')}
          >
            Web Development
          </button>
          <button 
            className={`filter-btn ${activeFilter === 'ecommerce' ? 'active' : ''}`}
            onClick={() => setActiveFilter('ecommerce')}
          >
            E-Commerce
          </button>
          <button 
            className={`filter-btn ${activeFilter === 'mobile' ? 'active' : ''}`}
            onClick={() => setActiveFilter('mobile')}
          >
            Mobile Apps
          </button>
          <button 
            className={`filter-btn ${activeFilter === 'enterprise' ? 'active' : ''}`}
            onClick={() => setActiveFilter('enterprise')}
          >
            Enterprise Solutions
          </button>
        </div>
      </section>

      <section className="portfolio-showcase">
        <div className="portfolio-container">
          {filteredProjects.map((project) => (
            <div key={project.id} className="portfolio-card" data-category={project.category}>
              <div className="portfolio-image">
                <img src={project.image} alt={project.title} />
              </div>
              <div className="portfolio-details">
                <h3>{project.title}</h3>
                <div className="portfolio-tags">
                  {project.tags.map((tag, index) => (
                    <span key={index}>{tag}</span>
                  ))}
                </div>
                <p>{project.description}</p>
                <div className="portfolio-links">
                  <Link to={`/portfolio/${project.id}`} className="btn-view-project">
                    View Project
                  </Link>
                  <Link to={`/portfolio/${project.id}/case-study`} className="btn-case-study">
                    Case Study
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="portfolio-stats">
        <div className="stat-container">
          <div className="stat-item">
            <i className="fas fa-check-circle"></i>
            <h3>200+</h3>
            <p>Projects Completed</p>
          </div>
          <div className="stat-item">
            <i className="fas fa-users"></i>
            <h3>150+</h3>
            <p>Happy Clients</p>
          </div>
          <div className="stat-item">
            <i className="fas fa-code-branch"></i>
            <h3>15+</h3>
            <p>Technologies</p>
          </div>
          <div className="stat-item">
            <i className="fas fa-trophy"></i>
            <h3>25+</h3>
            <p>Awards Won</p>
          </div>
        </div>
      </section>

      <section className="portfolio-cta">
        <div className="cta-content">
          <h2>Ready to Start Your Project?</h2>
          <p>Let's create something amazing together</p>
          <Link to="/contact" className="cta-button">Get in Touch</Link>
        </div>
      </section>
    </div>
  );
};

export default Portfolio; 